/**
 * @flow
 */
import Box from '@mui/material/Box';
import { IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import QuestionErrorMessage from 'components/@home/drawers/FormDrawer/QuestionErrorMessage';
import QuestionOptions from 'components/@home/drawers/FormDrawer/QuestionOptions';
import QuestionTypeIcon from 'components/@home/drawers/FormDrawer/QuestionTypeIcon';
import { hasOptions } from 'components/@home/drawers/FormDrawer/questionTypes';
import type { Question } from 'components/@home/drawers/FormDrawer/Types';
import { ErrorMessage, Field, FieldArray, useFormikContext } from 'formik';
import DeleteIcon from 'mdi-react/DeleteIcon';
import React from 'react';
import { I18n } from 'utils/i18n';
import focusToEnd from 'utils/focusToEnd';
import keyIs, { DOWN, ENTER } from 'utils/keyIs';
import useFocusRef from 'utils/useFocusRef';

const QuestionTitleInput = styled('input')(({ theme }) => ({
  color: theme.palette.text.primary,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
  width: '100%',
  lineHeight: '22px',
}));

type Props = {
  question: Question,
  index: number,
  remove: Function,
};

const QuestionForm = ({ question, index, remove }: Props) => {
  const formik = useFormikContext();

  const { error } = formik.getFieldMeta(`questions.${index}`);
  const showErrors = [];
  if (formik.submitCount > 0 && typeof error === 'object') {
    Object.keys(error).forEach(field => {
      const { touched } = formik.getFieldMeta(`questions.${index}.${field}`);
      if (touched) {
        showErrors.push(field);
      }
    });
  }

  const focusRef = useFocusRef();

  const nextOnEnter = e => {
    if (keyIs(e, ENTER, DOWN)) {
      const input = e.target.nextSibling?.querySelector('input');
      focusToEnd(input);
      e.stopPropagation();
      e.preventDefault();
    }
  };

  let description = I18n.t(`FormDrawer.QuestionTypes.${question.type}.description`);
  if (description === 'description') {
    description = null;
  }
  return (
    <Box
      sx={theme => ({
        background: theme.palette.secondary.ultraLight,
        borderRadius: '6px',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        mb: '10px',
      })}
    >
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ fontSize: 18, mr: '20px' }}>
          <QuestionTypeIcon type={question.type} text={`${index + 1}`} />
        </Box>
        <Box sx={{ width: '100%' }}>
          <Field name={`questions.${index}.title`}>
            {({ field }) => (
              <QuestionTitleInput
                {...field}
                placeholder={I18n.t('FormDrawer.QuestionField.placeholder')}
                id={question._id}
                ref={focusRef}
                onKeyDown={nextOnEnter}
              />
            )}
          </Field>
          {description && <Typography variant="caption">{description}</Typography>}
          {hasOptions(question.type) ? (
            <FieldArray name={`questions.${index}.options`} component={QuestionOptions} />
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flex: '1 0 auto',
          alignItems: 'center',
          mb: '-20px',
          mr: '-20px',
        }}
      >
        <Box sx={{ flex: '1 0 auto' }}>
          {showErrors.map(field => (
            <ErrorMessage
              key={field}
              name={`questions.${index}.${field}`}
              component={QuestionErrorMessage}
            />
          ))}
        </Box>
        <IconButton
          onClick={() => remove(index)}
          sx={{ color: 'secondary.main', fontSize: 11 }}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default QuestionForm;
